'use client'

import { IconArrowLeft } from '@macarongfactory/web-icons'
import { SolidButton, Tab, Text, TopNavigation } from '@macarongfactory/web-ui'
import { useRouter, useSearchParams } from 'next/navigation'
import { useRef } from 'react'

import { MycleLogoDefault } from '@/shared/assets/icons'
import { MYCLE_BRIDGE_COMMANDS, requestBridge } from '@/shared/lib/appBridge'
import { ROUTES } from '@/shared/lib/routes'

import useCarStoryHomeQuery from '../api/query/useCarStoryHomeQuery'
import { CAR_STORY_APP_DOWNLOAD_LINKS } from '../lib/appDownloadLinks'
import { carStoryTracking } from '../lib/tracking'
import { MYCLE_CONTENT_MENU, type TMycleContentMenu } from '../model/home'
import useCarStoryHasNew from '../model/useCarStoryHasNew'
import useTrackOnVisible from '../model/useTrackOnVisible'

const TAB_ITEMS = [
  { text: '홈', key: 'home', menu: null },
  { text: '차량관리', key: 'maintenance', menu: MYCLE_CONTENT_MENU.CAR },
  { text: '드라이브', key: 'drive', menu: MYCLE_CONTENT_MENU.DRIVE },
  { text: 'NOW', key: 'now', menu: MYCLE_CONTENT_MENU.NOW },
] as const satisfies ReadonlyArray<{
  text: string
  key: string
  menu: TMycleContentMenu | null
}>

const DEFAULT_TAB = 'home'

const FROM_BANNER = 'banner'

const CarStoryTopNavigation = () => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const currentTab = searchParams.get('tab') ?? DEFAULT_TAB
  const isFromBanner = searchParams.get('from') === FROM_BANNER

  const pcDownloadButtonRef = useRef<HTMLDivElement | null>(null)
  useTrackOnVisible({
    ref: pcDownloadButtonRef,
    tracker: carStoryTracking.showDownloadButton,
  })

  const { data: homeData } = useCarStoryHomeQuery()
  const { hasNewCar, hasNewDrive, hasNewNow, markVisited } = useCarStoryHasNew(
    homeData?.latestPublishedAt
  )

  const hasNewByMenu = {
    [MYCLE_CONTENT_MENU.CAR]: hasNewCar,
    [MYCLE_CONTENT_MENU.DRIVE]: hasNewDrive,
    [MYCLE_CONTENT_MENU.NOW]: hasNewNow,
  } as const

  const handleTabClick = (tab: (typeof TAB_ITEMS)[number]) => {
    if (tab.menu) markVisited(tab.menu)
    carStoryTracking.clickTab(tab.text)
    // 탭 전환은 history 엔트리를 쌓지 않도록 replace 사용
    router.replace(ROUTES.CAR_STORY.TAB(tab.key))
  }

  const handleDownloadClick = () => {
    carStoryTracking.clickDownloadButton()
    window.location.href = CAR_STORY_APP_DOWNLOAD_LINKS.CAR_STORY_TOP_NAVIGATION
  }

  return (
    <div className='sticky top-0 z-50 bg-white'>
      {/* Mobile */}
      <div className='md:hidden'>
        <TopNavigation
          title={<TopNavigation.Title.Text text='차량 이야기' />}
          leading={
            isFromBanner ? undefined : (
              <TopNavigation.Leading.IconButton
                icon={<IconArrowLeft width={24} height={24} />}
                clickable={() =>
                  requestBridge.Mycle({
                    command: MYCLE_BRIDGE_COMMANDS.CLOSE_WEBVIEW,
                  })
                }
              />
            )
          }
        />
      </div>
      {/* PC */}
      <div
        ref={pcDownloadButtonRef}
        className='gap-dimension-x3 px-dimension-x5 h-dimension-x14 hidden items-center bg-white md:flex'
      >
        <div className='gap-dimension-x3 flex min-w-0 flex-1 items-center'>
          <MycleLogoDefault fill='#00AFFF' width='120' height='24' />
          <Text
            variant='heading-3-semibold'
            color='text-foreground-neutral-strong'
          >
            차량 이야기
          </Text>
        </div>
        <SolidButton
          text='앱 다운로드'
          variant='brand'
          size='small'
          clickable={handleDownloadClick}
        />
      </div>
      <Tab scrollable={false}>
        {TAB_ITEMS.map(tab => (
          <Tab.TabItem
            key={tab.key}
            text={tab.text}
            selected={currentTab === tab.key}
            badge={tab.menu && hasNewByMenu[tab.menu] ? 'dot' : undefined}
            clickable={() => handleTabClick(tab)}
          />
        ))}
      </Tab>
    </div>
  )
}

export default CarStoryTopNavigation
